import config from '../../config';

class CookieHelper {
  getCookie(name) {
    const cookiesArray = document.cookie.split(`; `);
    const cookieArray = cookiesArray.filter(item => {
      const cookieKey = item.split(`=`)[0];
      return cookieKey === name;
    });
    if(cookieArray.length === 1) {
      return decodeURIComponent(cookieArray[0].split(`=`)[1]);
    }
    return false;
  }

  getAllCookieNames() {
    const cookiesArray = document.cookie.split(`; `);
    const cookieArray = cookiesArray.filter(item => {
      return item.split(`=`)[0];
    });
    return cookieArray.map(cookie => decodeURIComponent((cookie.split(`=`)[0])));
  }

  /**
   * @param {number} seconds
   * @return {{expires: string, 'max-age': number}}
   */
  getCookieOptions(seconds) {
    return {
      expires: new Date(Date.now() + seconds * 1000).toUTCString(),
      'max-age': seconds,
    };
  }

  removeCookie(name, useDomain = true, domain = config.cookie.domain) {
    const domainString = useDomain ? `domain=${domain};` : ``;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;;path=/;${domainString}`;
  }

  removeAll() {
    this.removeAuthToken();
    this.removeCartId();
    this.removeCartSession();
    // this.removeCurrency();
    // this.removeLanguage();
    this.removeCartIdsForCheckout();
    this.removeMerchandiseCartId();
  }

  setCookie(name, value, options = null, cnnRetry = false) {
    if(!cnnRetry && !this.checkIfCookieIsAccepted(name, value, options)) {
      return;
    }

    let cookie = `${name}=${encodeURIComponent(value)};path=/;domain=${config.cookie.domain};`;

    if(options) {
      for(const option of Object.keys(options)) {
        cookie += `${option}=${options[option]};`;
      }
    }

    // set default duration
    if(!options || (options && !options.expires && !options[`max-age`])) {
      cookie += `max-age=${config.cookie.maxAgeInSeconds};expires=${new Date(Date.now() + config.cookie.maxAgeInSeconds * 1000).toUTCString()};`;
    }

    // add secure flag
    cookie += window.location.protocol !== `http:` ? `Secure;` : ``;
    cookie += `SameSite=Lax`;

    document.cookie = cookie;
  }

  /**
   *
   * @returns {boolean} Cookie Notice accepted or not
   */
  checkIfCookiesCanBeSet() {
    return !!this.getCookie(`ccn-interacted`);
  }

  checkIfCookieIsAccepted(name, value, options) {
    const acceptedCookiesRaw = this.getCookie(`ccn-interacted`);
    if(!acceptedCookiesRaw) {
      const eventListener = () => {
        this.setCookie(name, value, options, true);
        document.removeEventListener(`ccn-set-${name}`, eventListener);
      };
      document.addEventListener(`ccn-set-${name}`, eventListener);
      return false;
    }
    const acceptedCookies = JSON.parse(acceptedCookiesRaw);
    return !!acceptedCookies.find(accepted => accepted === name);
  }

  /**
   * @param name {string}
   * @param callback {function | boolean = false}
   * @returns {boolean}
   */
  checkIfExternalCookieIsAccepted(name, callback = false) {
    const acceptedCookiesRaw = this.getCookie(`ccn-interacted`);
    if(!acceptedCookiesRaw || !acceptedCookiesRaw.includes(name)) {
      const eventListener = () => {
        if(callback) {
          callback();
        }
        document.removeEventListener(`ccn-set-${name}`, eventListener);
      };
      document.addEventListener(`ccn-set-${name}`, eventListener);
      return false;
    }
    const acceptedCookies = JSON.parse(acceptedCookiesRaw);
    return !!acceptedCookies.find(accepted => accepted === name);
  }

  getAuthToken() {
    return this.getCookie(`rin-token`);
  }

  removeAuthToken() {
    this.removeCookie(`rin-token`);
  }

  setAuthToken(value) {
    const options = this.getCookieOptions(config.cookie.maxAgeInSeconds);
    this.setCookie(`rin-token`, value, options);
  }

  getLastUsedCartId() {
    return parseInt(this.getCookie(`rin-cart-id`), 10);
  }

  removeCartId() {
    this.removeCookie(`rin-cart-id`);
  }

  removeCart() {
    this.removeCartId();
    this.removeCartSession();
    this.removeCartIdsForCheckout();
  }

  async setLastUsedCartId(value) {
    if(!value) {
      return;
    }
    this.setCookie(`rin-cart-id`, value);
    const event = await document.createEvent(`Event`);
    event.initEvent(`rin-cart-id-update`, false, true);
    document.dispatchEvent(event);
  }

  getCartSession() {
    let session = this.getCookie(`rin-session`);
    if(session) {
      session = session.replace(`%7B`, `{`);
      session = session.replace(`%7D`, `}`);
    }
    return session;
  }

  removeCartSession() {
    this.removeCookie(`rin-session`);
  }

  setCartSession(value) {
    this.setCookie(`rin-session`, value);
  }

  getCurrency() {
    const cur = this.getCookie(`rin-currency`);
    if(cur) return cur;
    return `EUR`;
  }

  removeCurrency() {
    this.removeCookie(`rin-currency`);
  }

  setCurrency(value) {
    this.setCookie(`rin-currency`, value);
  }

  getLanguage() {
    const browserLang = navigator.language || navigator.userLanguage; // navigator.userLanguage for IE11
    let lang = browserLang.split(`-`)[0];

    if(this.checkIfCookiesCanBeSet()) {
      lang = this.getCookie(`rin-language`) || `de`;
    }
    return lang;
  }

  removeLanguage() {
    this.removeCookie(`rin-language`);
  }

  setLanguage(value) {
    const oneYearInS = 365 * 60 * 60 * 24;
    const options = this.getCookieOptions(oneYearInS);
    this.setCookie(`rin-language`, value, options);
  }

  setCartIdsForCheckout(value) {
    this.setCookie(`rin-carts-for-checkout`, value.toString());
  }

  getCartIdsForCheckout() {
    const val = this.getCookie(`rin-carts-for-checkout`);
    return val ? val.split(`%2C`) : [];
  }

  removeCartIdsForCheckout() {
    this.removeCookie(`rin-carts-for-checkout`);
  }

  getMerchandiseCartId() {
    return parseInt(this.getCookie(`rin-merchandise-cart-id`), 10);
  }

  removeMerchandiseCartId() {
    this.removeCookie(`rin-merchandise-cart-id`);
  }

  setMerchandiseCartId(value) {
    this.setCookie(`rin-merchandise-cart-id`, value);
  }

  getBouncerWebSocketConnectionJWT() {
    return this.getCookie(`rin-bouncer-waiting-token`);
  }

  removeBouncerWebSocketConnectionJWT() {
    this.removeCookie(`rin-bouncer-waiting-token`);
  }

  setBouncerWebSocketConnectionJWT(value) {
    const oneDayInSeconds = 60 * 60 * 24;
    const options = this.getCookieOptions(oneDayInSeconds);
    this.setCookie(`rin-bouncer-waiting-token`, value, options);
  }

  getBouncerShoppingJWT() {
    return this.getCookie(`rin-bouncer-token`);
  }

  removeBouncerShoppingJWT() {
    this.removeCookie(`rin-bouncer-token`);
  }

  setBouncerShoppingJWT(value) {
    const tenMinutesInSeconds = config.bouncer.config.endTimeOffsetInMinutes * 60;
    const options = this.getCookieOptions(tenMinutesInSeconds);
    this.setCookie(`rin-bouncer-token`, value, options);
  }

  getBouncerShoppingStartTime() {
    return this.getCookie(`rin-bouncer-shopping-starttime`);
  }

  removeBouncerShoppingStartTime() {
    this.removeCookie(`rin-bouncer-shopping-starttime`);
  }

  setBouncerShoppingStartTime(value) {
    const tenMinutesInSeconds = config.bouncer.config.endTimeOffsetInMinutes * 60;
    const options = this.getCookieOptions(tenMinutesInSeconds);
    this.setCookie(`rin-bouncer-shopping-starttime`, value, options);
  }
}

export default new CookieHelper();
